import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import { getLastOrderPaymentMethod } from "../../../../helpers/orderPaymentStorage";
import {
  filterCheckoutMethodsByEffective,
  getMethodsWithLegacyFallback,
} from "../../../../services/paymentMethodSettingsService";
import {
  CheckoutPaymentLine,
  OrderPaymentMethod,
  TerminalCardType,
} from "../../../../services/orderService";

interface CheckoutPaymentModalProps {
  isOpen: boolean;
  toggle: () => void;
  submitting: boolean;
  tenantId: number;
  totalAmountCents: number;
  subtotalAmountCents?: number | null;
  discountTotalCents?: number;
  onConfirm: (payments: CheckoutPaymentLine[]) => void;
}

type EditablePaymentLine = {
  key: string;
  payment_method: OrderPaymentMethod | "";
  amountInput: string;
};

const newLineKey = (): string =>
  `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const CheckoutPaymentModal = ({
  isOpen,
  toggle,
  submitting,
  tenantId,
  totalAmountCents,
  subtotalAmountCents = null,
  discountTotalCents = 0,
  onConfirm,
}: CheckoutPaymentModalProps) => {
  const { t, i18n } = useTranslation();
  const [lines, setLines] = useState<EditablePaymentLine[]>([]);
  const [availableMethods, setAvailableMethods] = useState<OrderPaymentMethod[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [methodsLoadError, setMethodsLoadError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [rememberedMethod, setRememberedMethod] = useState(false);
  const [autoFillHint, setAutoFillHint] = useState<string | null>(null);
  const [terminalCardType, setTerminalCardType] = useState<TerminalCardType | "">("");

  useEffect(() => {
    if (!isOpen) {
      setLines([]);
      setAvailableMethods([]);
      setLoadingMethods(false);
      setMethodsLoadError(null);
      setValidationError(null);
      setRememberedMethod(false);
      setAutoFillHint(null);
      setTerminalCardType("");
      return;
    }

    let cancelled = false;

    const loadMethods = async () => {
      setLoadingMethods(true);
      setMethodsLoadError(null);

      try {
        const response = await getMethodsWithLegacyFallback();
        const methods = filterCheckoutMethodsByEffective(response.data.effective.checkout);

        if (cancelled) {
          return;
        }

        setAvailableMethods(methods);

        const stored = getLastOrderPaymentMethod(tenantId);
        const defaultMethod =
          stored && methods.includes(stored) ? stored : methods[0] ?? "";

        setLines([
          {
            key: newLineKey(),
            payment_method: defaultMethod,
            amountInput: formatCentsToMoneyInput(totalAmountCents, i18n.language),
          },
        ]);
        setRememberedMethod(Boolean(stored && methods.includes(stored)));
        setAutoFillHint(null);
      } catch (error: any) {
        if (!cancelled) {
          setMethodsLoadError(
            error?.data || t("studio.orders.checkout.payment.toast.loadMethodsError")
          );
          setAvailableMethods([]);
          setLines([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingMethods(false);
        }
      }
    };

    void loadMethods();

    return () => {
      cancelled = true;
    };
  }, [isOpen, tenantId, t, totalAmountCents, i18n.language]);

  const parsedAmounts = useMemo(
    () => lines.map((line) => parseMoneyInputToCents(line.amountInput, i18n.language)),
    [lines, i18n.language]
  );

  const paymentsSum = parsedAmounts.reduce((sum, amount) => sum + amount, 0);
  const remainingCents = totalAmountCents - paymentsSum;
  const hasAvailableMethods = availableMethods.length > 0;
  const pixGatewayCount = lines.filter(
    (line) => line.payment_method === "pix_gateway"
  ).length;
  const terminalGatewayCount = lines.filter(
    (line) => line.payment_method === "card_terminal_gateway"
  ).length;
  const usesTerminalGateway = terminalGatewayCount > 0;

  const applyAmountWithAutoBalance = (
    key: string,
    amountInput: string,
    balancePartner: boolean
  ) => {
    setLines((previous) => {
      const index = previous.findIndex((line) => line.key === key);

      if (index < 0) {
        return previous;
      }

      const next = previous.map((line) =>
        line.key === key ? { ...line, amountInput } : line
      );

      if (!balancePartner || next.length !== 2) {
        return next;
      }

      const editedCents = parseMoneyInputToCents(amountInput, i18n.language);
      const partnerIndex = index === 0 ? 1 : 0;
      const partnerAmount = Math.max(totalAmountCents - editedCents, 0);

      next[partnerIndex] = {
        ...next[partnerIndex],
        amountInput: formatCentsToMoneyInput(partnerAmount, i18n.language),
      };

      return next;
    });
    setRememberedMethod(false);
    setValidationError(null);
    setAutoFillHint(
      balancePartner
        ? t("studio.orders.checkout.payment.autoBalanceHint")
        : null
    );
  };

  const updateLine = (key: string, patch: Partial<EditablePaymentLine>) => {
    if (patch.amountInput !== undefined) {
      applyAmountWithAutoBalance(key, patch.amountInput, lines.length === 2);
      return;
    }

    setLines((previous) =>
      previous.map((line) => (line.key === key ? { ...line, ...patch } : line))
    );
    setRememberedMethod(false);
    setValidationError(null);
  };

  const fillRemainingOnLine = (key: string) => {
    setLines((previous) => {
      const othersSum = previous.reduce((sum, line) => {
        if (line.key === key) {
          return sum;
        }

        return sum + parseMoneyInputToCents(line.amountInput, i18n.language);
      }, 0);
      const remaining = Math.max(totalAmountCents - othersSum, 0);

      return previous.map((line) =>
        line.key === key
          ? {
              ...line,
              amountInput: formatCentsToMoneyInput(remaining, i18n.language),
            }
          : line
      );
    });
    setAutoFillHint(t("studio.orders.checkout.payment.fillRemainingHint"));
    setValidationError(null);
  };

  const handleAddLine = () => {
    const defaultMethod =
      availableMethods.find((method) => !lines.some((line) => line.payment_method === method))
      ?? availableMethods[0]
      ?? "";

    setLines((previous) => {
      if (previous.length === 0) {
        return [
          {
            key: newLineKey(),
            payment_method: defaultMethod,
            amountInput: formatCentsToMoneyInput(totalAmountCents, i18n.language),
          },
        ];
      }

      const currentAmounts = previous.map((line) =>
        parseMoneyInputToCents(line.amountInput, i18n.language)
      );
      const currentSum = currentAmounts.reduce((sum, amount) => sum + amount, 0);
      let working = previous;

      // Se a única forma está com o total inteiro, libera metade para a nova linha.
      if (previous.length === 1 && currentSum === totalAmountCents && totalAmountCents > 1) {
        const firstShare = Math.floor(totalAmountCents / 2);
        working = [
          {
            ...previous[0],
            amountInput: formatCentsToMoneyInput(firstShare, i18n.language),
          },
        ];
      }

      const workingSum = working.reduce(
        (sum, line) => sum + parseMoneyInputToCents(line.amountInput, i18n.language),
        0
      );
      const remaining = Math.max(totalAmountCents - workingSum, 0);

      return [
        ...working,
        {
          key: newLineKey(),
          payment_method: defaultMethod,
          amountInput: formatCentsToMoneyInput(remaining, i18n.language),
        },
      ];
    });

    setAutoFillHint(t("studio.orders.checkout.payment.autoFillOnAddHint"));
    setValidationError(null);
  };

  const handleRemoveLine = (key: string) => {
    setLines((previous) => {
      if (previous.length <= 1) {
        return previous;
      }

      const next = previous.filter((line) => line.key !== key);

      if (next.length === 1) {
        return [
          {
            ...next[0],
            amountInput: formatCentsToMoneyInput(totalAmountCents, i18n.language),
          },
        ];
      }

      return next;
    });
    setAutoFillHint(null);
    setValidationError(null);
  };

  const handleSubmit = () => {
    if (!hasAvailableMethods) {
      return;
    }

    if (lines.some((line) => !line.payment_method)) {
      setValidationError(
        t("studio.orders.checkout.payment.validation.paymentMethodRequired")
      );
      return;
    }

    if (pixGatewayCount > 1) {
      setValidationError(t("studio.orders.checkout.payment.validation.pixGatewayMaxOne"));
      return;
    }

    if (terminalGatewayCount > 1) {
      setValidationError(
        t("studio.orders.checkout.payment.validation.terminalGatewayMaxOne")
      );
      return;
    }

    if (usesTerminalGateway && lines.length !== 1) {
      setValidationError(
        t("studio.orders.checkout.payment.validation.terminalGatewaySolo")
      );
      return;
    }

    if (usesTerminalGateway && !terminalCardType) {
      setValidationError(
        t("studio.orders.checkout.payment.validation.terminalCardTypeRequired")
      );
      return;
    }

    if (parsedAmounts.some((amount) => amount < 1)) {
      setValidationError(t("studio.orders.checkout.payment.validation.amountMin"));
      return;
    }

    if (paymentsSum !== totalAmountCents) {
      setValidationError(t("studio.orders.checkout.payment.validation.sumMismatch"));
      return;
    }

    const payments: CheckoutPaymentLine[] = lines.map((line, index) => ({
      payment_method: line.payment_method as OrderPaymentMethod,
      amount_cents: parsedAmounts[index],
      ...(line.payment_method === "card_terminal_gateway" && terminalCardType
        ? { terminal_card_type: terminalCardType }
        : {}),
    }));

    setValidationError(null);
    onConfirm(payments);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        {t("studio.orders.checkout.payment.modalTitle")}
      </ModalHeader>
      <ModalBody>
        {loadingMethods ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" size="sm" />
          </div>
        ) : methodsLoadError ? (
          <Alert color="danger" className="mb-0">
            {methodsLoadError}
          </Alert>
        ) : !hasAvailableMethods ? (
          <Alert color="warning" className="mb-0">
            <p className="mb-3">{t("studio.checkout.empty_methods")}</p>
            <Button color="primary" size="sm" tag={Link} to="/settings/payments" onClick={toggle}>
              {t("studio.checkout.configure_payments")}
            </Button>
          </Alert>
        ) : (
          <>
            <p className="text-muted mb-3">
              {t("studio.orders.checkout.payment.totalLabel", {
                total: formatMoneyFromCents(totalAmountCents, i18n.language),
              })}
            </p>
            {discountTotalCents > 0 ? (
              <Alert color="info" className="py-2 small">
                {t("studio.orders.checkout.payment.discountApplied", {
                  subtotal: formatMoneyFromCents(
                    subtotalAmountCents ?? totalAmountCents + discountTotalCents,
                    i18n.language
                  ),
                  discount: formatMoneyFromCents(discountTotalCents, i18n.language),
                  total: formatMoneyFromCents(totalAmountCents, i18n.language),
                })}
              </Alert>
            ) : null}
            {rememberedMethod && lines.length === 1 ? (
              <p className="text-muted small mb-2">
                {t("studio.orders.checkout.payment.rememberedHint")}
              </p>
            ) : null}
            {autoFillHint ? (
              <Alert color="info" className="py-2 small">
                {autoFillHint}
              </Alert>
            ) : null}

            {lines.map((line, index) => (
              <div key={line.key} className="border rounded p-3 mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="form-label mb-0">
                    {t("studio.orders.checkout.payment.lineLabel", { n: index + 1 })}
                  </Label>
                  <div className="d-flex gap-2">
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      onClick={() => fillRemainingOnLine(line.key)}
                      disabled={submitting}
                    >
                      {t("studio.orders.checkout.payment.fillRemaining")}
                    </Button>
                    {lines.length > 1 ? (
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => handleRemoveLine(line.key)}
                        disabled={submitting}
                      >
                        {t("studio.orders.checkout.payment.removeLine")}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-md-7">
                    <Input
                      type="select"
                      value={line.payment_method}
                      onChange={(event) =>
                        updateLine(line.key, {
                          payment_method: event.target.value as OrderPaymentMethod | "",
                        })
                      }
                      disabled={submitting}
                    >
                      <option value="">
                        {t("studio.orders.checkout.payment.methodPlaceholder")}
                      </option>
                      {availableMethods.map((method) => (
                        <option key={method} value={method}>
                          {t(`studio.orders.checkout.payment.method.${method}`)}
                        </option>
                      ))}
                    </Input>
                  </div>
                  <div className="col-md-5">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={line.amountInput}
                      onChange={(event) =>
                        updateLine(line.key, { amountInput: event.target.value })
                      }
                      disabled={submitting}
                      placeholder={t("studio.orders.checkout.payment.amountPlaceholder")}
                    />
                  </div>
                </div>
                {line.payment_method === "card_terminal_gateway" ? (
                  <div className="mt-3">
                    <Label className="form-label">
                      {t("studio.orders.checkout.payment.terminal.cardTypeLabel")}
                    </Label>
                    <div className="d-flex flex-wrap gap-3">
                      {(["debit", "credit"] as TerminalCardType[]).map((type) => (
                        <div className="form-check" key={type}>
                          <Input
                            className="form-check-input"
                            type="radio"
                            name={`terminal-card-type-${line.key}`}
                            id={`terminal-card-type-${line.key}-${type}`}
                            checked={terminalCardType === type}
                            onChange={() => {
                              setTerminalCardType(type);
                              setValidationError(null);
                            }}
                            disabled={submitting}
                          />
                          <Label
                            className="form-check-label"
                            for={`terminal-card-type-${line.key}-${type}`}
                          >
                            {t(`studio.orders.checkout.payment.terminal.cardType.${type}`)}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <p className="text-muted small mb-0 mt-2">
                      {t("studio.orders.checkout.payment.terminal.methodHint")}
                    </p>
                  </div>
                ) : null}
              </div>
            ))}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <Button
                color="light"
                size="sm"
                onClick={handleAddLine}
                disabled={
                  submitting
                  || availableMethods.length === 0
                  || usesTerminalGateway
                }
              >
                {t("studio.orders.checkout.payment.addLine")}
              </Button>
              <span
                className={
                  remainingCents === 0 ? "text-success small" : "text-muted small"
                }
              >
                {remainingCents === 0
                  ? t("studio.orders.checkout.payment.sumOk")
                  : t("studio.orders.checkout.payment.remainingLabel", {
                      remaining: formatMoneyFromCents(remainingCents, i18n.language),
                    })}
              </span>
            </div>

            {validationError && (
              <FormFeedback className="d-block">{validationError}</FormFeedback>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle} disabled={submitting}>
          {t("studio.orders.actions.cancel")}
        </Button>
        <Button
          color="success"
          onClick={handleSubmit}
          disabled={submitting || loadingMethods || !hasAvailableMethods}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("studio.orders.loading")}
            </>
          ) : (
            t("studio.orders.checkout.payment.confirmPayment")
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CheckoutPaymentModal;
